import ModalType from '@/Enums/Segments/ModalType.enum';
import type { IParsedSegment, IParsedSingleSegment } from '@/Interfaces/Segments/Segment.interface';
import { Grid } from '@/components/Grid';
import Pagination from '@/components/Grid/Pagination';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsAdmin } from '@/store/selectors/me.selectors';
import { selectSegmentsFilters, selectSegmentsSegments } from '@/store/selectors/segments.selectors';
import { resetSelectedSegment } from '@/store/slices/segments.slice';
import { getSelectedSegment, updateActiveStatus } from '@/store/slices/segments.thunks';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { type GridFilterModel, GridLogicOperator } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import SegmentModal from '../SegmentModal';
import ToolBar from './ToolBar';
import { getColumns } from './columns';

interface IProps {
  loadSegments: () => void;
}

const SegmentsGrid = ({ loadSegments }: IProps) => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(selectIsAdmin);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterLogicOperator: GridLogicOperator.Or,
    logicOperator: GridLogicOperator.Or,
    quickFilterValues: [],
  });

  const onFilterModelChange = (newFilterModel: GridFilterModel) => setFilterModel(newFilterModel);

  const filters = useAppSelector(selectSegmentsFilters);
  const { segments, loadingSegments } = useAppSelector(selectSegmentsSegments);

  //#region dispatch
  const getCurrentSegment = useCallback(
    (id: IParsedSegment['id']) => {
      dispatch(getSelectedSegment({ id }));
    },
    [dispatch],
  );

  const resetCurrentSegment = useCallback(() => {
    dispatch(resetSelectedSegment());
  }, [dispatch]);
  //#endregion

  //#region modals
  const [modalType, setModalType] = useState(ModalType.ViewSegment);
  const [modalOpen, setModalOpen] = useState(false);

  const onNewSegmentClose = useCallback(
    (newSegment?: IParsedSingleSegment) => {
      if (!newSegment) {
        setModalOpen(false);
        return;
      }
      setModalType(ModalType.ViewSegment);
      getCurrentSegment(newSegment.id);
      loadSegments();
    },
    [getCurrentSegment, loadSegments],
  );

  const onCreateNewClick = useCallback(() => {
    setModalOpen(true);
    setModalType(ModalType.NewSegment);
  }, []);

  const onViewSegmentClose = useCallback(
    (segment?: Partial<IParsedSingleSegment>) => {
      setModalOpen(false);
      resetCurrentSegment();
      if (segment) {
        loadSegments();
      }
    },
    [resetCurrentSegment, loadSegments],
  );
  const onViewSegmentClick = useCallback(
    (row: IParsedSegment) => {
      getCurrentSegment(row.id);
      setModalType(ModalType.ViewSegment);
      setModalOpen(true);
    },
    [getCurrentSegment],
  );

  const archiveSegment = useCallback(
    (segment: { id: number; active: boolean }) => {
      dispatch(updateActiveStatus(segment));
    },
    [dispatch],
  );

  const onModalClose = useCallback(
    (segment?: Partial<IParsedSingleSegment> | IParsedSingleSegment) => {
      if (modalType === ModalType.ViewSegment) {
        onViewSegmentClose(segment);
        return;
      }
      if (modalType === ModalType.NewSegment) {
        onNewSegmentClose(segment as IParsedSingleSegment);
        return;
      }
    },
    [modalType, onNewSegmentClose, onViewSegmentClose],
  );
  //#endregion

  //#region columns
  const COLUMNS = useMemo(
    () => getColumns(onViewSegmentClick, archiveSegment, isAdmin),
    [archiveSegment, onViewSegmentClick, isAdmin],
  );
  //#endregion

  return (
    <>
      <Grid
        loading={loadingSegments}
        autosizeOnMount
        autosizeOptions={EXPAND_OPTIONS}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        showCellVerticalBorder
        showColumnVerticalBorder
        rows={segments}
        columns={COLUMNS}
        columnHeaderHeight={30}
        initialState={{
          pagination: { paginationModel: { pageSize: 15 } },
          sorting: { sortModel: [{ field: 'segmentId', sort: 'desc' }] },
        }}
        pagination
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        slots={{
          pagination: Pagination,
          toolbar: ToolBar,
          columnSortedAscendingIcon: KeyboardArrowUp,
          columnSortedDescendingIcon: KeyboardArrowDown,
        }}
        slotProps={{
          pagination: { loading: loadingSegments },
          footer: { className: 'relative' },
          toolbar: {
            setQuickFilter: quickFilterValues => setFilterModel(prev => ({ ...prev, quickFilterValues })),
            onCreateNewClick,
            active: filters.active,
            loading: loadingSegments,
          },
        }}
      />
      <SegmentModal onClose={onModalClose} open={modalOpen} modalType={modalType} />
    </>
  );
};

export default SegmentsGrid;
